import Parse from "parse";

import type { _User } from "./_User";
import type { SIV_DeliveryNotes } from "./SIV_DeliveryNotes";
import type { SIV_Order } from "./SIV_Order";
import type { OD3_Tenant } from "./OD3_Tenant";

export interface SIV_PackageAttributes {
  id: string;
  objectId: string;
  createdAt: Date;
  updatedAt: Date;
  changedAt?: Date | undefined;
  changedBy?: _User | undefined;
  constructionType?: string | undefined;
  customerCategory?: string | undefined;
  customerPackageNo?: string | undefined;
  deliveryDate?: Date | undefined;
  deliveryNote?: SIV_DeliveryNotes | undefined;
  directory?: string | undefined;
  directoryDate?: Date | undefined;
  grossWeight?: number | undefined;
  netWeight?: number | undefined;
  no: string;
  notFoundValues: any;
  order: SIV_Order;
  packageAssignments: any[];
  packageHeight?: number | undefined;
  packageLength?: number | undefined;
  packageNo?: string | undefined;
  packageWidth?: number | undefined;
  packagingCategory?: string | undefined;
  packagingDate?: Date | undefined;
  packagingEndDate?: Date | undefined;
  packagingLocation?: string | undefined;
  packagingType?: string | undefined;
  packedBy?: string | undefined;
  productionEndDate?: Date | undefined;
  productionFacility?: string | undefined;
  shortDescription?: string | undefined;
  sivFacility?: string | undefined;
  statusCompletion?: string | undefined;
  statusInvoice?: string | undefined;
  statusProcess?: string | undefined;
  storageInstruction?: string | undefined;
  surface?: number | undefined;
  tenant?: OD3_Tenant | undefined;
  transportCode?: string | undefined;
  volume?: number | undefined;
  xcellize?: string | undefined;
}

export class SIV_Package extends Parse.Object<SIV_PackageAttributes> {
  static className: string = "SIV_Package";

  constructor(data?: Partial<SIV_PackageAttributes>) {
    super("SIV_Package", data as SIV_PackageAttributes);
  }

  get changedAt(): Date | undefined {
    return super.get("changedAt");
  }
  set changedAt(value: Date | undefined) {
    super.set("changedAt", value);
  }

  get changedBy(): _User | undefined {
    return super.get("changedBy");
  }
  set changedBy(value: _User | undefined) {
    super.set("changedBy", value);
  }

  get constructionType(): string | undefined {
    return super.get("constructionType");
  }
  set constructionType(value: string | undefined) {
    super.set("constructionType", value);
  }

  get customerCategory(): string | undefined {
    return super.get("customerCategory");
  }
  set customerCategory(value: string | undefined) {
    super.set("customerCategory", value);
  }

  get customerPackageNo(): string | undefined {
    return super.get("customerPackageNo");
  }
  set customerPackageNo(value: string | undefined) {
    super.set("customerPackageNo", value);
  }

  get deliveryDate(): Date | undefined {
    return super.get("deliveryDate");
  }
  set deliveryDate(value: Date | undefined) {
    super.set("deliveryDate", value);
  }

  get deliveryNote(): SIV_DeliveryNotes | undefined {
    return super.get("deliveryNote");
  }
  set deliveryNote(value: SIV_DeliveryNotes | undefined) {
    super.set("deliveryNote", value);
  }

  get directory(): string | undefined {
    return super.get("directory");
  }
  set directory(value: string | undefined) {
    super.set("directory", value);
  }

  get directoryDate(): Date | undefined {
    return super.get("directoryDate");
  }
  set directoryDate(value: Date | undefined) {
    super.set("directoryDate", value);
  }

  get grossWeight(): number | undefined {
    return super.get("grossWeight");
  }
  set grossWeight(value: number | undefined) {
    super.set("grossWeight", value);
  }

  get netWeight(): number | undefined {
    return super.get("netWeight");
  }
  set netWeight(value: number | undefined) {
    super.set("netWeight", value);
  }

  get no(): string {
    return super.get("no");
  }
  set no(value: string) {
    super.set("no", value);
  }

  get notFoundValues(): any {
    return super.get("notFoundValues");
  }
  set notFoundValues(value: any) {
    super.set("notFoundValues", value);
  }

  get order(): SIV_Order {
    return super.get("order");
  }
  set order(value: SIV_Order) {
    super.set("order", value);
  }

  get packageAssignments(): any[] {
    return super.get("packageAssignments");
  }
  set packageAssignments(value: any[]) {
    super.set("packageAssignments", value);
  }

  get packageHeight(): number | undefined {
    return super.get("packageHeight");
  }
  set packageHeight(value: number | undefined) {
    super.set("packageHeight", value);
  }

  get packageLength(): number | undefined {
    return super.get("packageLength");
  }
  set packageLength(value: number | undefined) {
    super.set("packageLength", value);
  }

  get packageNo(): string | undefined {
    return super.get("packageNo");
  }
  set packageNo(value: string | undefined) {
    super.set("packageNo", value);
  }

  get packageWidth(): number | undefined {
    return super.get("packageWidth");
  }
  set packageWidth(value: number | undefined) {
    super.set("packageWidth", value);
  }

  get packagingCategory(): string | undefined {
    return super.get("packagingCategory");
  }
  set packagingCategory(value: string | undefined) {
    super.set("packagingCategory", value);
  }

  get packagingDate(): Date | undefined {
    return super.get("packagingDate");
  }
  set packagingDate(value: Date | undefined) {
    super.set("packagingDate", value);
  }

  get packagingEndDate(): Date | undefined {
    return super.get("packagingEndDate");
  }
  set packagingEndDate(value: Date | undefined) {
    super.set("packagingEndDate", value);
  }

  get packagingLocation(): string | undefined {
    return super.get("packagingLocation");
  }
  set packagingLocation(value: string | undefined) {
    super.set("packagingLocation", value);
  }

  get packagingType(): string | undefined {
    return super.get("packagingType");
  }
  set packagingType(value: string | undefined) {
    super.set("packagingType", value);
  }

  get packedBy(): string | undefined {
    return super.get("packedBy");
  }
  set packedBy(value: string | undefined) {
    super.set("packedBy", value);
  }

  get productionEndDate(): Date | undefined {
    return super.get("productionEndDate");
  }
  set productionEndDate(value: Date | undefined) {
    super.set("productionEndDate", value);
  }

  get productionFacility(): string | undefined {
    return super.get("productionFacility");
  }
  set productionFacility(value: string | undefined) {
    super.set("productionFacility", value);
  }

  get shortDescription(): string | undefined {
    return super.get("shortDescription");
  }
  set shortDescription(value: string | undefined) {
    super.set("shortDescription", value);
  }

  get sivFacility(): string | undefined {
    return super.get("sivFacility");
  }
  set sivFacility(value: string | undefined) {
    super.set("sivFacility", value);
  }

  get statusCompletion(): string | undefined {
    return super.get("statusCompletion");
  }
  set statusCompletion(value: string | undefined) {
    super.set("statusCompletion", value);
  }

  get statusInvoice(): string | undefined {
    return super.get("statusInvoice");
  }
  set statusInvoice(value: string | undefined) {
    super.set("statusInvoice", value);
  }

  get statusProcess(): string | undefined {
    return super.get("statusProcess");
  }
  set statusProcess(value: string | undefined) {
    super.set("statusProcess", value);
  }

  get storageInstruction(): string | undefined {
    return super.get("storageInstruction");
  }
  set storageInstruction(value: string | undefined) {
    super.set("storageInstruction", value);
  }

  get surface(): number | undefined {
    return super.get("surface");
  }
  set surface(value: number | undefined) {
    super.set("surface", value);
  }

  get tenant(): OD3_Tenant | undefined {
    return super.get("tenant");
  }
  set tenant(value: OD3_Tenant | undefined) {
    super.set("tenant", value);
  }

  get transportCode(): string | undefined {
    return super.get("transportCode");
  }
  set transportCode(value: string | undefined) {
    super.set("transportCode", value);
  }

  get volume(): number | undefined {
    return super.get("volume");
  }
  set volume(value: number | undefined) {
    super.set("volume", value);
  }

  get xcellize(): string | undefined {
    return super.get("xcellize");
  }
  set xcellize(value: string | undefined) {
    super.set("xcellize", value);
  }
}

Parse.Object.registerSubclass("SIV_Package", SIV_Package);
